import { useEffect, useMemo, useRef, useState } from 'react';
import { getAuth, User } from 'firebase/auth';
import { useObservable, useSubscription } from 'observable-hooks';
import { BehaviorSubject, combineLatest, concatMap, Subject } from 'rxjs';

import {
	TReplications,
	useBusinessLayer,
	useUser,
	wipeFEStorages,
} from '@mopla/business-logic';
import { IWhoami } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';

export const useManageRxDBReplications = () => {
	const { replications, db } = useBusinessLayer();
	const { userData: moplaUser } = useUser();
	const [auth] = useState(getAuth());
	const logger = useLogger('RxDBReplications');
	const prevFirebaseUserRef = useRef<User | null>(null);

	const { firebaseUser$, moplaUser$ } = useMemo(
		() => ({
			firebaseUser$: new Subject<User | null>(),
			moplaUser$: new BehaviorSubject<IWhoami | null>(null),
		}),
		[]
	);

	const processReplications = async (
		firebaseUser: User | null,
		moplaUser: IWhoami | null
	) => {
		/**
		 * Here we address the case where a user was authenticated some time ago (thus has the "user" local document),
		 * but later opens a page when the id/refresh tokens are all expired.
		 * In this case we want to cleanup the rxdb, which would potentially contain the old data
		 * */
		if (!firebaseUser && !prevFirebaseUserRef.current) {
			const userLocalDoc = await db.getLocal('user');
			if (userLocalDoc) {
				// `firebaseUser` comes from `onAuthStateChanged`, which only fires once
				// Firebase has settled the persisted auth state — so a `null` here means
				// there is genuinely no session (logout / expired), not a cold-start
				// race. Wipe the stale local document so it can't migrate to the next
				// user who signs in on this device. (The spurious force-logout MC-11571
				// was about is prevented by the 401 handling in getAuthToken /
				// handle401Effect, not by keeping stale data here.)
				logger.warn(
					'No firebase session at cold start; wiping stale local data',
					{
						reason: 'confirmed-no-session',
					}
				);
				await wipeFEStorages({ db, replications });
				window.location.reload();
				return;
			}
		}

		prevFirebaseUserRef.current = firebaseUser;

		/** We process all the available replications, starting them if the conditions are met */
		const replicationPromises = Object.values(
			replications as TReplications
		).map(async (replication) => {
			const hasReadyUser =
				firebaseUser && moplaUser && moplaUser.pleaseComplete === false;

			if (
				!replication.isAuthProtected ||
				(replication.isAuthProtected && hasReadyUser)
			) {
				await replication.start();
			}
		});

		await Promise.all(replicationPromises);
	};

	const replicationObservable = useObservable(() =>
		combineLatest([firebaseUser$, moplaUser$]).pipe(
			concatMap(([firebaseUser, moplaUser]) =>
				processReplications(firebaseUser, moplaUser)
			)
		)
	);

	useSubscription(replicationObservable);

	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged((user) =>
			firebaseUser$.next(user)
		);
		return () => unsubscribe();
	}, [auth, firebaseUser$]);

	useEffect(() => {
		moplaUser$.next(moplaUser);
	}, [moplaUser, moplaUser$]);
};
